import type { LabelPrintJob, LabelSizeId, SparePartLabelData } from "./types";
import type { LabelBuildOptions } from "./types";

export interface SparePartForLabel {
  id: string;
  name: string;
  serial_no?: string | null;
  qr_token: string;
  brand?: string | null;
  model_number?: string | null;
  harvested_at: string;
}

export interface SparePartLabelBuildOptions {
  size?: LabelSizeId;
  locale?: string;
  captions?: SparePartLabelData["captions"];
}

export function sparePartDisplayId(part: SparePartForLabel): string {
  return part.serial_no || part.qr_token.slice(0, 12).toUpperCase();
}

export function sparePartQrUrl(part: SparePartForLabel): string {
  return `${window.location.origin}/spare-part/${part.serial_no || part.qr_token}`;
}

export function sparePartToLabelData(
  part: SparePartForLabel,
  options: Omit<SparePartLabelBuildOptions, "size"> = {},
): SparePartLabelData {
  return {
    qrValue: sparePartQrUrl(part),
    partName: part.name,
    partId: sparePartDisplayId(part),
    sourceBrand: part.brand,
    sourceModel: part.model_number,
    harvestedAt: part.harvested_at,
    captions: options.captions,
  };
}

/**
 * Build one data-oriented print job per spare part.
 * ZPL is rendered later by `buildZpl` / `useLabelPrinter.printJobs`.
 */
export function buildSparePartPrintJobs(
  parts: SparePartForLabel[],
  options: SparePartLabelBuildOptions = {},
): LabelPrintJob<"spare-part">[] {
  const jobOptions: LabelBuildOptions = {
    size: options.size,
    locale: options.locale,
  };
  return parts.map((part) => ({
    kind: "spare-part" as const,
    data: sparePartToLabelData(part, options),
    options: jobOptions,
  }));
}
